import React from 'react';
import { useSelector } from 'react-redux';
import { styled, useTheme } from '@mui/material/styles';

// Domain imports.
import { Heading, ImageDialog, Loader } from '@domain/components';
import { useSaveIncidentFactory, } from '@domain/hooks';
import { getPrevNextPagePath, generatePageContent, chooseLogoSize, checkIsFeedback } from '@domain/helpers';

import { validationSchema, validationSchemaNotRequired } from './validation-schema';
import { detectContentType } from '@domain/content';
import { useQuestionnaire } from '@domain/providers';
import { FilesUploadPage } from '../../../shared/pages';
import { useLocation } from 'react-router-dom';

const instructions = [
  {
    title: 'Klik hier om een voorbeeld te zien van het kentekenbewijs',
    imageAlt: 'kentekenbewijs',
    imagePath: `/img/kentekenbewijs.png`,
    className: 'kentekenbewijs--image',
  }
];

const imageDetails = {
  imageAlt: 'kentekenbewijs',
  imagePath: `/img/kentekenbewijs.png`,
  className: 'kentekenbewijs--image',
}

const StyledHeading = styled(Heading)`
padding-top: 20px;
`

function LicensePlateRegister() {


  const { questionnaire } = useQuestionnaire();

  const themeContext = useTheme();

  const incident = useSelector(state => state.incident);
  const insurer = useSelector(state => state.insurer);
  const files = useSelector(state => state.files)

  const lpRegisterImages = files.registrationCertificate

  const location = useLocation()
  const currentPath = location.pathname;
  const page = questionnaire.find(page => page.path === currentPath)
  const saveBehavior = page ? page.saveBehavior : undefined

  const saveIncident = useSaveIncidentFactory(incident, saveBehavior);

  //Handle image modal
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };


  if (!lpRegisterImages || !insurer || !incident || !questionnaire) {
    return <Loader />;
  }

  const isFeedback = checkIsFeedback(incident)

  const pagePath = getPrevNextPagePath(
    questionnaire,
    incident,
    currentPath,
    files,
    isFeedback
  );
  const nextPage = pagePath.nextPage !== currentPath
    ? pagePath.nextPage
    : undefined;

  const isPageBeforeSuccess = questionnaire[questionnaire.length - 2].path === currentPath

  function feedbackCause() {
    const page = questionnaire.find(page => page.name === 'licenseplate_register')
    const feedbacktext = page.feedbacktext ? decodeURIComponent(page.feedbacktext) : null
    return feedbacktext
  }

  const contentType = detectContentType(insurer)
  const pageName = questionnaire.find(page => page.path === currentPath).name
  const content = generatePageContent(contentType, insurer, incident, false, false, pageName)

  const legend = content["over-title-text"]
  const heading = content["title"]
  const customHelpLink = content["custom-help-link"]
  const helpLinkText = content["help-link-text"]
  const useCustomLogo = content["use-custom-logo"]
  const mobileParagraph = content["mobile-text"]
  const firstParagraph = content["intro-text"]
  const extraBlockText = content["extra-block-text"]
  const faqs = content["faqs"]
  const validationText = content["validation-text"]

  const validationSchemaToBeUsed = () => {
    if (page.required === false) {
      return validationSchemaNotRequired
    }
    return validationSchema(validationText)
  }

  const faqsMobile = faqs.concat({
    question: "Heeft u een voorbeeld van een kentekenbewijs?",
    answer: (
      <dl>
        {instructions.map(({ title, explanation }, i) => (
          <React.Fragment key={title}>
            <div className="small__image"><img className={imageDetails.className} src={imageDetails.imagePath} alt={imageDetails.imageAlt} onClick={handleOpen} /></div>
          </React.Fragment>
        ))}
      </dl>
    ),
  });

  const middleLogo = {
    src: insurer.logoURL || '',
    alt: insurer.brandName || 'uw verzekeraar',
    width: '100%',
    height: chooseLogoSize(themeContext.logoSize),
  }

  const extraLeftBlock = (<React.Fragment>
    <StyledHeading level="4">
      <b>{extraBlockText}</b>
    </StyledHeading >
    <div className="small__image"><img className={imageDetails.className} src={imageDetails.imagePath} alt={imageDetails.imageAlt} onClick={handleOpen} /></div>
    <ImageDialog handleClose={handleClose} open={open} src={imageDetails.imagePath} alt={imageDetails.imageAlt} />
  </React.Fragment>
  )

  const dialogMobile = (<ImageDialog handleClose={handleClose} open={open} src={imageDetails.imagePath} alt={imageDetails.imageAlt} />)
  return (
    <FilesUploadPage
      nextPage={nextPage}
      prevPage={pagePath.prevPage}
      questionnaire={questionnaire}
      saveIncident={saveIncident}
      buttonText="Volgende"
      currentPath={currentPath}
      filesSetName="registrationCertificate"
      filesSet={lpRegisterImages}
      feedbackCause={feedbackCause()}
      validationSchema={validationSchemaToBeUsed()}
      faqs={faqs}
      faqsMobile={faqsMobile}
      legend={legend}
      heading={heading}
      mobileParagraph={mobileParagraph}
      firstParagraph={firstParagraph}
      files={files}
      incident={incident}
      insurer={insurer}
      multiple={false}
      isPageBeforeSuccess={isPageBeforeSuccess}
      showDesktopParagraphOnMobile={true}
      extraLeftBlock={extraLeftBlock}
      dialogMobile={dialogMobile}
      isFeedback={isFeedback}
      customHelpLink={customHelpLink}
      helpLinkText={helpLinkText}
      customMiddleLogo={useCustomLogo}
      middleLogo={middleLogo}
    />
  )
}

export default LicensePlateRegister
